"use client";

import { TextField } from "@/components/tool-kit/primitives";
import { LABEL } from "@/components/tool-kit/styles";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import type { SampleSummary } from "@/lib/tool-kit/stats-tests/t-test";

/*
 * Mean, SD and n for one sample — the t-test calculator's summary-statistics input.
 * Words arrive through `labels`.
 */

export interface SummaryText {
  mean: string;
  sd: string;
  n: string;
}

export const EMPTY_SUMMARY: SummaryText = { mean: "", sd: "", n: "" };

const SUMMARY_SEPARATOR = "|";

export function readSummary(text: SummaryText): SampleSummary | null {
  const mean = parseNumberInput(text.mean);
  const sd = parseNumberInput(text.sd);
  const n = parseIntegerInput(text.n);
  if (mean === null || sd === null || n === null) {
    return null;
  }
  return { mean, sd, n };
}

/** "10.2|2.1|15" → the three fields, for "Try an example". */
export function splitSummary(value: string): SummaryText {
  const [mean = "", sd = "", n = ""] = value.split(SUMMARY_SEPARATOR);
  return { mean, sd, n };
}

export function SummaryFields({
  prefix,
  title,
  value,
  onChange,
  labels,
}: {
  prefix: string;
  title: string;
  value: SummaryText;
  onChange: (next: SummaryText) => void;
  labels: { meanLabel: string; sdLabel: string; nLabel: string };
}) {
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{title}</legend>
      <div className="grid gap-3 sm:grid-cols-3">
        <TextField
          id={`${prefix}-mean`}
          label={labels.meanLabel}
          numeric
          onChange={(mean) => onChange({ ...value, mean })}
          value={value.mean}
        />
        <TextField
          id={`${prefix}-sd`}
          label={labels.sdLabel}
          numeric
          onChange={(sd) => onChange({ ...value, sd })}
          value={value.sd}
        />
        <TextField
          id={`${prefix}-n`}
          label={labels.nLabel}
          numeric
          onChange={(n) => onChange({ ...value, n })}
          value={value.n}
        />
      </div>
    </fieldset>
  );
}
